import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import type {
  Address,
  ContactInput,
  ContactResponse,
} from "@guestbook/shared";
import type { ApiClientError } from "@/shared/lib/api";
import { Button } from "@/shared/components/ui/Button";
import { FormField } from "@/shared/components/form/FormField";
import { TextareaFormField } from "@/shared/components/form/TextareaFormField";
import { SelectFormField } from "@/shared/components/form/SelectFormField";
import { AddressFields } from "@/shared/components/form/AddressFields";
import { useHouseholdsList } from "@/shared/lib/households";

const EMPTY_ADDRESS: Address = {
  line1: null,
  line2: null,
  city: null,
  postalCode: null,
  countryCode: null,
  countryName: null,
  stateCode: null,
  stateName: null,
};

const NO_HOUSEHOLD = "__none__" as const;

interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  notes: string;
  tags: string;
  householdId: string;
  address: Address;
}

function tagsFromString(s: string): string[] {
  return s
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

function tagsToString(tags: string[] | undefined): string {
  return (tags ?? []).join(", ");
}

function emptyState(): ContactFormState {
  return {
    name: "",
    email: "",
    phone: "",
    notes: "",
    tags: "",
    householdId: NO_HOUSEHOLD,
    address: { ...EMPTY_ADDRESS },
  };
}

function stateFromContact(contact: ContactResponse): ContactFormState {
  return {
    name: contact.name ?? "",
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    notes: contact.notes ?? "",
    tags: tagsToString(contact.tags),
    householdId: contact.householdId ?? NO_HOUSEHOLD,
    address: contact.address ?? { ...EMPTY_ADDRESS },
  };
}

export interface ContactFormProps {
  mode: "create" | "edit";
  initialContact?: ContactResponse;
  submitLabel: string;
  pending: boolean;
  error: ApiClientError | null;
  onSubmit: (input: ContactInput) => void;
}

/**
 * Shared contact form used by both `new` and `edit` routes. The component
 * is a thin controlled wrapper around the spec'd contact body — it does
 * not own the mutation, so callers can wire success/error UX per page.
 */
export function ContactForm({
  mode,
  initialContact,
  submitLabel,
  pending,
  error,
  onSubmit,
}: ContactFormProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<ContactFormState>(() =>
    initialContact ? stateFromContact(initialContact) : emptyState(),
  );
  const householdsQuery = useHouseholdsList({ limit: 100 });

  function patch<K extends keyof ContactFormState>(key: K, value: ContactFormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const input: ContactInput = {
      name: state.name.trim(),
      email: state.email.trim() || null,
      phone: state.phone.trim() || null,
      notes: state.notes.trim() || null,
      tags: tagsFromString(state.tags),
      householdId: state.householdId === NO_HOUSEHOLD ? null : state.householdId,
      address: state.address,
      customFields: initialContact?.customFields ?? {},
    };
    onSubmit(input);
  }

  const householdOptions = [
    { value: NO_HOUSEHOLD, label: "No household" },
    ...(householdsQuery.data?.items.map((hh) => ({
      value: hh.id,
      label: hh.displayNameCustom ?? "Unnamed household",
    })) ?? []),
  ];

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
      <section className="flex flex-col gap-6">
        <h2 className="type-heading-sm text-text">Name & contact</h2>
        <FormField
          label="Name"
          required
          value={state.name}
          onChange={(e) => patch("name", e.target.value)}
          autoComplete="name"
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label="Email"
            type="email"
            value={state.email}
            onChange={(e) => patch("email", e.target.value)}
            autoComplete="email"
          />
          <FormField
            label="Phone"
            type="tel"
            value={state.phone}
            onChange={(e) => patch("phone", e.target.value)}
            autoComplete="tel"
            hint="Include country code for international numbers."
          />
        </div>
      </section>

      <AddressFields
        value={state.address}
        onChange={(next) => patch("address", next)}
      />

      <section className="flex flex-col gap-6">
        <h2 className="type-heading-sm text-text">Grouping</h2>
        <SelectFormField
          label="Household"
          options={householdOptions}
          value={state.householdId}
          onValueChange={(next) => patch("householdId", next as string)}
          hint="Contacts in a household share one mailing address at export time."
        />
        <FormField
          label="Tags"
          value={state.tags}
          onChange={(e) => patch("tags", e.target.value)}
          hint="Comma-separated. Example: family, college, wedding."
        />
      </section>

      <section className="flex flex-col gap-6">
        <h2 className="type-heading-sm text-text">Notes</h2>
        <TextareaFormField
          label="Notes"
          rows={4}
          value={state.notes}
          onChange={(e) => patch("notes", e.target.value)}
        />
      </section>

      {error ? (
        <p className="type-body-sm text-danger-strong mx-4">{error.message}</p>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending}>
          {pending
            ? mode === "create"
              ? "Creating…"
              : "Saving…"
            : submitLabel}
        </Button>
      </div>
    </form>
  );
}
